import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { trackSignupConversion } from '../services/analytics';
import './Register.css';

const Register: React.FC = () => {
  const { student, register } = useAuth();
  const navigate = useNavigate();

  // Onboarding intentionally minimal: first name (required), school (optional),
  // stream (required — subjects won't render without it). Last name, profile
  // name, and grade are defaulted server-side so we don't force them up front.
  const [formData, setFormData] = useState({
    first_name: '',
    school_name: '',
    stream: '' as 'natural' | 'social' | '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [naturalSubjects, setNaturalSubjects] = useState<string[]>([]);
  const [socialSubjects, setSocialSubjects] = useState<string[]>([]);

  // Fetch subjects for both streams
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        // Fetch natural science subjects
        const naturalResponse = await fetch('/api/subjects/natural');
        if (naturalResponse.ok) {
          const naturalData = await naturalResponse.json();
          setNaturalSubjects(naturalData.subjects.map((s: any) => s.name));
        }

        // Fetch social science subjects
        const socialResponse = await fetch('/api/subjects/social');
        if (socialResponse.ok) {
          const socialData = await socialResponse.json();
          setSocialSubjects(socialData.subjects.map((s: any) => s.name));
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
        // Fallback to hardcoded values
        setNaturalSubjects(['Biology', 'Chemistry', 'Physics', 'Mathematics', 'English']);
        setSocialSubjects(['History', 'Geography', 'Economics', 'Mathematics', 'English']);
      }
    };

    fetchSubjects();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.stream) {
      setError('Please select your stream');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await register({
        ...formData,
        last_name: '',
        profile_name: formData.first_name,
        grade_level: 12,
        phone_number: student?.phone_number || '',
      });
      trackSignupConversion();
      // Redirect to subscription selection after registration
      navigate('/subscription-selection');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <div className="register-logo">
            <Logo height={52} />
          </div>
          <h1>Let's Set You Up for Success</h1>
          <p className="subtitle">Quick profile setup, then you're ready to crush the ESSLCE or the AAU UAT</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Your first name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="school_name">School Name <span className="label-optional">(optional)</span></label>
            <input
              type="text"
              id="school_name"
              name="school_name"
              value={formData.school_name}
              onChange={handleChange}
              placeholder="e.g., Medhane Alem School"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Select Your Stream</label>
            <div className="stream-options">
              <label className={`stream-option ${formData.stream === 'natural' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="stream"
                  value="natural"
                  checked={formData.stream === 'natural'}
                  onChange={handleChange}
                  disabled={loading}
                />
                <div className="stream-content">
                  <span className="stream-icon">🔬</span>
                  <span className="stream-name">Natural Science</span>
                  <span className="stream-subjects">
                    {naturalSubjects.length > 0 ? naturalSubjects.join(', ') : 'Loading...'}
                  </span>
                </div>
              </label>

              <label className={`stream-option ${formData.stream === 'social' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="stream"
                  value="social"
                  checked={formData.stream === 'social'}
                  onChange={handleChange}
                  disabled={loading}
                />
                <div className="stream-content">
                  <span className="stream-icon">🌍</span>
                  <span className="stream-name">Social Science</span>
                  <span className="stream-subjects">
                    {socialSubjects.length > 0 ? socialSubjects.join(', ') : 'Loading...'}
                  </span>
                </div>
              </label>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-button" disabled={loading || !formData.stream}>
            {loading ? 'Setting up...' : 'Let\'s Go! 🚀'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
